import i18n from 'i18next'
import { format, parseISO } from 'date-fns'
import { es } from 'date-fns/locale'
import type { IGreenfieldColumn } from '../../types/greenfield-response'

type DataItem = { [key: string]: string }
type MappedItem = { [fieldName: string]: string }

export function mapFieldNames(
    ref: IGreenfieldColumn[],
    data: DataItem[],
): MappedItem[] {
    if (!ref) return []
    const refMap: Record<string, string> = ref.reduce(
        (acc: Record<string, string>, { ref_id, field_name }) => {
            acc[ref_id] = field_name
            return acc
        },
        {},
    )

    return data.map((item) => {
        const mappedItem: MappedItem = {}
        for (const [key, value] of Object.entries(item)) {
            if (refMap[key]) {
                mappedItem[refMap[key]] = value
            }
        }
        return mappedItem
    })
}

// Locations returns store hours as wall-clock times ("HH:MM:SS") in the store's
// own time zone, and uses 23:59:59 as the end-of-day sentinel for stores that
// close at midnight (this renders as "11:59 PM", matching target.com). The date
// string is intentionally parsed without a trailing "Z" so it is treated as
// local wall-clock time and displayed as-is, with no device-time-zone shift.
export const formatStoreTime = (time: string) => {
    return new Intl.DateTimeFormat('en-US', {
        hour: 'numeric',
        minute: 'numeric',
        hour12: true,
    }).format(new Date(`1970-01-01T${time}`))
}

export const translateStoreFactsApiString = (
    t: (key: string, options?: Record<string, unknown>) => string,
    apiString: string,
): string => {
    if (!apiString) return apiString

    const tOpts = { nsSeparator: false as const }

    // Try the original string first, then lowercased (API sends "Tuesday" but keys are "tuesday")
    const candidates = [apiString, apiString.toLocaleLowerCase()]
    for (const candidate of candidates) {
        if (i18n.exists(candidate)) {
            return t(candidate, tOpts)
        }
    }

    // No translation found — return original string silently
    return apiString
}

export const formatDateWithLocale = (
    dateStr: string,
    formatString: string,
    currentLanguage: string,
): string => {
    try {
        const date = parseISO(dateStr)
        const locale = currentLanguage === 'es' ? es : undefined
        return format(date, formatString, { locale })
    } catch {
        throw new Error('Invalid date')
    }
}
